<template>
  <DashboardContentLayout>
    <template
      v-if="!isLoading"
      #title>
      <span class="text-32">
        {{ fullName || $t('pages.onboarding.admin_page.not_specified') }}
      </span>
    </template>
    <template
      v-if="!isLoading"
      #title-actions>
      <RiskLevelDropdown
        :risk-level="store.riskLevel"
        :application-status="store.applicationStatus"
        :change-handler="store.updateRiskLevel"/>
    </template>
    <template
      v-if="!isLoading"
      #actions>
      <StatusDropdown
        v-if="store.blocked && store.applicationStatus !== 'CLOSED'"
        application-status="BLOCKED"
        :sections="[]"
        :verification-in-progress="false"/>
      <StatusDropdown
        :application-status="store.applicationStatus"
        :sections="uncompletedSections"
        :completed-all-user-stages="completedAllUserStages"
        @on-status-change="onApplicationStatusChange"
        @on-reject="declineApplication"/>
    </template>
    <template v-if="!isLoading">
      <TabLinks
        :tabs="tabs"
        secondary/>
    </template>
    <router-view v-slot="{ Component }">
      <Transition name="page">
        <template v-if="!isLoading">
          <component :is="Component"/>
        </template>
        <template v-else>
          <LoaderStub class="items-center"/>
        </template>
      </Transition>
    </router-view>
  </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { successNotification } from '@/_redesign/shared/ui/NotificationPopup/notification-popup';
import LoaderStub from '@/components/loader-stub.vue';
import TabLinks from '@/components/ui-kit/app-tab-links.vue';
import DashboardContentLayout from '@/layouts/dashboard/dashboard-content-layout.vue';
import RiskLevelDropdown from '@/modules/onboarding-dashboard/shared/components/RiskLevelDropdown.vue';
import StatusDropdown from '@/modules/onboarding-dashboard/shared/components/StatusDropdown.vue';
import { useCustomerProfileProvide } from '@/modules/onboarding-dashboard/shared/utils';
import { ONBOARDING_PERSONAL_PROFILE_CHILDREN as tabs } from '@/modules/onboarding-personal-profile/routes';
import { requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { OnboardingAdminRequests } from '@/services/requests/onboarding/OnboardingRequests';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';
import { IApiResponse } from '@/types/interfaces/ApiResponse.interface';
import { getRouteParam } from '@/utils/getRouteParam';
import { pollerFunction } from '@/utils/pollerFunction';
const store = usePersonalOnboardingStore();

const { t } = useI18n();

const isLoading = ref(true);
const isVerificationInProgress = ref(false);
const id = getRouteParam(useRoute().params.id);

const fullName = computed(() => {
  if (!store.personalDetails?.firstName && !store.personalDetails?.lastName) {
    return '';
  }

  return [
    store.personalDetails?.firstName,
    store.personalDetails?.lastName,
  ].join(' ');
});

useCustomerProfileProvide('PERSONAL');

const uncompletedSections = computed(() => {
  // DEBT: add check for other sections
  const sections: string[] = [];

  const stages = [
    { key: 'PERSONAL_DETAILS', label: 'pages.onboarding.admin_page.personal_information' },
    { key: 'PROOF_OF_IDENTITY', label: 'pages.onboarding.admin_page.proof_of_identity' },
    { key: 'PROOF_OF_ADDRESS', label: 'pages.onboarding.admin_page.proof_of_address' },
    { key: 'RISK_LEVEL', label: 'pages.onboarding.admin_page.risk_level' },
    { key: 'CONTRACT', label: 'pages.onboarding.admin_page.plan' },
  ] as const;

  stages.forEach((stage) => {
    if (!store.completedStages.includes(stage.key)) {
      sections.push(t(stage.label));
    }
  });

  return sections;
});

const completedAllUserStages = computed(() => store.completedStages.includes('PRICING'));

async function readUserApplication() {
  isLoading.value = true;
  await store.fetchAdminApplication(id, true);
  isLoading.value = false;

  if (store.applicationStatus !== 'VERIFYING') return;

  isVerificationInProgress.value = true;
  const exitCondition = () => store.applicationStatus === 'VERIFIED';
  pollerFunction(() => store.fetchAdminApplication(id, true), exitCondition, 0, 3)
    .then(() => {
      successNotification(
        t(
          'pages.onboarding.business.company_details.verify.tooltip.success',
          { customer: 'customer' },
        ),
      );
    })
    .finally(() => {
      isVerificationInProgress.value = false;
    });
}

async function declineApplication() {
  await requestHandler(() => OnboardingAdminRequests.Personal.declineApplication(id), {
    pending: isLoading,
    onSuccess: async () => {
      await readUserApplication();
      successNotification(
        t(
          'pages.onboarding.business.company_details.decline.tooltip.success',
          { customer: 'customer' },
        ),
      );
    },
  });
}

async function onApplicationStatusChange(status: 'verified' | 'in-progress' | 'no-response' | 'non-verified' | 'onboarding') {
  let request: ((id: string) => Promise<IApiResponse<Onboarding.Personal.Application>>) | null = null;

  switch (status) {
    case 'verified':
      request = OnboardingAdminRequests.Personal.verifyApplication;
      break;
    case 'in-progress':
      request = OnboardingAdminRequests.Personal.inProgressApplication;
      break;
    case 'no-response':
      request = OnboardingAdminRequests.Personal.noResponseApplication;
      break;
    case 'non-verified':
      request = OnboardingAdminRequests.Personal.onNonVerifiedApplication;
      break;
    case 'onboarding':
      request = OnboardingAdminRequests.Personal.onOnboardingApplication;
      break;
    default:
      request = null;
  }

  if (!request) return;

  await requestHandler(() => request!(id), {
    pending: isLoading,
    onSuccess: async () => {
      await readUserApplication();
    },
  });
}

onBeforeMount(readUserApplication);
</script>
